import React, { useState } from 'react';
import { ArrowLeft, BarChart3, Briefcase, Minus, DollarSign, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import JobScheduler from './JobScheduler';
import FamilyJobsSection from './content/FamilyJobsSection';
import TransactionHistoryModal from './shared/TransactionHistoryModal';
import QuickWorkSchedule from '../Work/QuickWorkSchedule';

export default function KidsAllowanceView() {
  const { currentUser, kidAllowances, familyCalendar, recordKidSpending, allowanceTransactions } = useFamilyContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('schedule');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [workScheduleOpen, setWorkScheduleOpen] = useState(false);
  
  // Quick transaction state - only for spending
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Get kid's allowance data
  const myAllowance = kidAllowances[currentUser?.id] || {
    current_balance: 0,
    weekly_amount: 0,
    job_scheduler_enabled: false,
    hourly_rate: 0
  };

  // Get this week's work shifts from calendar
  const getThisWeekShifts = () => {
    if (!familyCalendar || !currentUser) return [];
    
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday
    startOfWeek.setHours(0, 0, 0, 0); // Start of day
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
    endOfWeek.setHours(23, 59, 59, 999); // End of day
    
    return familyCalendar.filter(event => {
      // Check multiple possible ways the work shift might be saved
      const isWorkShift = event.category === 'work' || 
                         event.type === 'work-shift' || 
                         event.activityType === 'work-shift' ||
                         event.title?.includes('Work Shift');
                         
      const isForThisUser = event.kidId === currentUser.id || 
                           event.createdBy === currentUser.id;
      
      if (!isWorkShift || !isForThisUser) return false;
      
      const eventDate = new Date(event.start_time || event.date);
      return eventDate >= startOfWeek && eventDate <= endOfWeek;
    });
  };

  const weekShifts = getThisWeekShifts();

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Handle kid's spending request
  const handleSpendingTransaction = async () => {
    if (!amount || !reason.trim()) return;
    
    const numAmount = parseFloat(amount);
    if (numAmount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    // Check if kid has enough balance for the purchase
    if (numAmount > myAllowance.current_balance) {
      alert(`You don't have enough allowance. Your balance is ${formatMoney(myAllowance.current_balance)}`);
      return;
    }

    setIsProcessing(true);
    try {
      await recordKidSpending(currentUser.id, numAmount, reason);
      
      // Reset form
      setAmount('');
      setReason('');
      
      // Show success message
      setSuccessMessage(`Successfully spent ${formatMoney(numAmount)} for ${reason}!`);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Spending error:', error);
      setSuccessMessage('Failed to record spending. Please try again.');
      
      // Clear error message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <TransactionHistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        kidId={currentUser?.id}
      />
      
      {workScheduleOpen && (
        <QuickWorkSchedule onClose={() => setWorkScheduleOpen(false)} />
      )}
      
      <div className="min-h-screen bg-stone-50">
        {/* Header */}
        <div className="bg-white border-b border-stone-200 sticky top-0 z-50">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-stone-600" />
                </button>
                <h1 className="text-lg font-bold text-slate-900">My Allowance</h1>
              </div>
              <button 
                onClick={() => setHistoryOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                <BarChart3 className="w-4 h-4" />
                History
              </button>
            </div>
          </div>
        </div>

        {/* Main Container */}
        <div className="max-w-[1200px] mx-auto p-4 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
          {/* Main Content */}
          <div className="space-y-4">
            {/* Balance Card */}
            <div className="relative bg-gradient-to-br from-slate-900 to-slate-950 rounded-xl p-6 text-white overflow-hidden">
              <div className="absolute top-[-50%] right-[-20%] w-32 h-32 bg-white/5 rounded-full transform rotate-45"></div>
              <div className="relative z-10">
                <div className="text-sm opacity-80 mb-1">Allowance Balance</div>
                <div className="text-3xl font-bold mb-1">{formatMoney(myAllowance.current_balance)}</div>
                <div className="text-xs opacity-70">Your available money</div>
              </div>
            </div>

            {/* Track Spending */}
            <div className="bg-white rounded-xl p-5 border border-stone-200 shadow-sm">
              <div className="text-center mb-5">
                <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-red-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900 mb-1">Track Spending</h2>
                <p className="text-xs text-stone-500">Record your purchases to keep track of your allowance</p>
              </div>

              {/* Spending Form */}
              <div className="space-y-4">
                {/* Amount Input */}
                <div>
                  <h3 className="text-center text-base font-semibold text-slate-900 mb-2">How much did you spend?</h3>
                  <div className="flex items-center justify-center">
                    <span className="text-2xl font-light text-stone-400 mr-1">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="text-2xl font-light border-none outline-none bg-transparent text-slate-900 w-32 text-left"
                      placeholder="0.00"
                      disabled={isProcessing}
                    />
                  </div>
                </div>

                {/* Reason Input */}
                <div>
                  <h3 className="text-center text-base font-semibold text-slate-900 mb-2">What did you buy?</h3>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-center text-slate-900 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="Toy, candy, game, etc."
                    disabled={isProcessing}
                  />
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSpendingTransaction}
                  disabled={!amount || !reason.trim() || parseFloat(amount) <= 0 || isProcessing}
                  className={`w-full py-3 rounded-lg text-sm font-semibold transition-all ${
                    amount && reason.trim() && parseFloat(amount) > 0 && !isProcessing
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                  }`}
                >
                  {isProcessing
                    ? 'Recording...'
                    : amount && reason.trim() && parseFloat(amount) > 0
                    ? `Record Spending ${formatMoney(parseFloat(amount))}`
                    : 'Enter amount and what you bought'
                  }
                </button>

                {/* Success Message */}
                {successMessage && (
                  <div className={`mt-4 p-3 rounded-lg text-sm font-medium ${
                    successMessage.includes('Successfully') 
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    {successMessage}
                  </div>
                )}
              </div>
            </div>

            {/* Work Schedule / Family Jobs Section */}
            {myAllowance.job_scheduler_enabled ? (
              <div className="bg-white rounded-xl p-5 border border-stone-200 border-l-4 border-l-azure-500">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-2 border-b border-stone-200">
                    <button
                      onClick={() => setActiveTab('schedule')}
                      className={`pb-2 px-3 text-sm font-medium transition-colors ${
                        activeTab === 'schedule' 
                          ? 'text-azure-600 border-b-2 border-azure-600' 
                          : 'text-stone-500 hover:text-stone-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        Job Scheduler
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('jobs')}
                      className={`pb-2 px-3 text-sm font-medium transition-colors ${
                        activeTab === 'jobs' 
                          ? 'text-azure-600 border-b-2 border-azure-600' 
                          : 'text-stone-500 hover:text-stone-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span>🏆</span>
                        Family Jobs
                      </div>
                    </button>
                  </div>
                  
                  {activeTab === 'schedule' && (
                    <button
                      onClick={() => setWorkScheduleOpen(true)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Schedule
                    </button>
                  )}
                </div>
                
                {activeTab === 'schedule' ? (
                  <JobScheduler 
                    shifts={weekShifts}
                    hourlyRate={myAllowance.hourly_rate}
                    onAddSchedule={() => setWorkScheduleOpen(true)}
                  />
                ) : (
                  <FamilyJobsSection />
                )}
              </div>
            ) : (
              <FamilyJobsSection />
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Recent Activity */}
            <div className="bg-white rounded-xl p-4 border border-stone-200">
              <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
                ⚡ Recent Activity
              </h3>
              {(() => {
                const myTransactions = allowanceTransactions[currentUser?.id] || [];
                const recentTransactions = myTransactions.slice(0, 5); // Show last 5 transactions
                
                if (recentTransactions.length === 0) {
                  return (
                    <div className="text-center text-stone-500 text-xs py-4">
                      No recent activity
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-2">
                    {recentTransactions.map((transaction, index) => (
                      <div key={transaction.id || index} className="flex items-center justify-between py-2 text-xs">
                        <div className="flex-1">
                          <div className="font-medium text-stone-900">
                            {transaction.type === 'addition' && '+ '}
                            {transaction.type === 'deduction' && '- '}
                            {transaction.type === 'weekly_addition' && '+ '}
                            {transaction.type === 'job_completion' && '+ '}
                            {formatMoney(transaction.amount)}
                          </div>
                          <div className="text-stone-500 truncate">
                            {transaction.reason}
                          </div>
                        </div>
                        <div className="text-stone-400 text-xs">
                          {new Date(transaction.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                    
                    {myTransactions.length > 5 && (
                      <button
                        onClick={() => setHistoryOpen(true)}
                        className="w-full text-center text-xs text-azure-600 hover:text-azure-700 py-2"
                      >
                        View all {myTransactions.length} transactions →
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Allowance Settings */}
            <div className="bg-white rounded-xl p-4 border border-stone-200">
              <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
                ⚙️ Allowance Settings
              </h3>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <div>
                    <div className="font-medium text-slate-900 text-sm">Weekly Allowance</div>
                    <div className="text-xs text-stone-500">Automatic distribution</div>
                  </div>
                  <div className="font-semibold text-slate-900 text-sm">{formatMoney(myAllowance.weekly_amount)}</div>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <div>
                    <div className="font-medium text-slate-900 text-sm">Distribution Day</div>
                    <div className="text-xs text-stone-500">When allowance is given</div>
                  </div>
                  <div className="font-semibold text-slate-900 text-sm">Sunday</div>
                </div>
                
                {myAllowance.job_scheduler_enabled && (
                  <>
                    <div className="flex justify-between items-center py-2 border-b border-stone-100">
                      <div>
                        <div className="font-medium text-slate-900 text-sm">Work Schedule</div>
                        <div className="text-xs text-stone-500">Job scheduler enabled</div>
                      </div>
                      <div className="font-semibold text-slate-900 text-sm">On</div>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-stone-100">
                      <div>
                        <div className="font-medium text-slate-900 text-sm">Hourly Rate</div>
                        <div className="text-xs text-stone-500">For work shifts</div>
                      </div>
                      <div className="font-semibold text-slate-900 text-sm">{formatMoney(myAllowance.hourly_rate)}/hr</div>
                    </div>
                  </>
                )}
                
                <div className="flex justify-between items-center py-2">
                  <div>
                    <div className="font-medium text-slate-900 text-sm">Job Notifications</div>
                    <div className="text-xs text-stone-500">Remind about jobs</div>
                  </div>
                  <div className="font-semibold text-slate-900 text-sm">On</div>
                </div>
              </div>
              
              <button className="w-full mt-3 bg-slate-900 hover:bg-slate-950 text-white py-2 rounded-lg text-sm font-medium transition-colors">
                View Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}